/**
 * App-wide keyboard focus behaviour that no single component can own
 * (UX-121 follow-ups). Installed once from main.tsx; every listener is
 * delegated on `document`, so it covers hand-rolled controls as well as the
 * primitives, and costs nothing until someone presses a key.
 *
 * 1. Radio groups (`[role="radiogroup"]` of `[role="radio"]` buttons) get the
 *    ARIA pattern: one Tab stop (the checked option), arrow keys / Home / End
 *    move between options and select as they go. Without it every chip in a
 *    service row was its own Tab stop (10 in the quote builder).
 * 2. A control that takes keyboard focus inside a horizontal scroll strip is
 *    scrolled fully into view, clear of the strip's edge fade, instead of
 *    stopping with its ring under the fade.
 *
 * Both only react to keyboard focus (`:focus-visible`), so a tap never moves
 * focus or scrolls a strip on its own.
 */

const RADIO = '[role="radio"]'
const GROUP = '[role="radiogroup"]'
/** Room left between a revealed control and the strip edge (the edge fade is 24px). */
export const STRIP_REVEAL_PAD = 32

function isKeyboardFocus(el: Element): boolean {
  try {
    return el.matches(':focus-visible')
  } catch {
    return true // very old engines: treat as keyboard
  }
}

function isDisabled(el: HTMLElement): boolean {
  return (el as HTMLButtonElement).disabled === true || el.getAttribute('aria-disabled') === 'true'
}

/** The options that belong to this group (not to a nested group), enabled only. */
export function groupRadios(group: Element): HTMLElement[] {
  return Array.from(group.querySelectorAll<HTMLElement>(RADIO)).filter(
    (r) => r.closest(GROUP) === group && !isDisabled(r) && !(r instanceof HTMLInputElement),
  )
}

/** One Tab stop per group: `current` gets tabindex 0, the rest -1. */
export function applyRovingTabIndex(group: Element, current?: HTMLElement | null) {
  const radios = groupRadios(group)
  if (radios.length === 0) return
  const stop =
    (current && radios.includes(current) ? current : null) ??
    radios.find((r) => r.getAttribute('aria-checked') === 'true') ??
    radios[0]
  for (const r of radios) r.tabIndex = r === stop ? 0 : -1
}

function radioOf(target: EventTarget | null): { radio: HTMLElement; group: Element } | null {
  if (!(target instanceof HTMLElement) || target instanceof HTMLInputElement) return null
  const radio = target.closest<HTMLElement>(RADIO)
  const group = radio?.closest(GROUP)
  return radio && group ? { radio, group } : null
}

function onRadioFocusIn(e: FocusEvent) {
  const hit = radioOf(e.target)
  if (!hit) return
  const { radio, group } = hit
  const fromOutside = !(e.relatedTarget instanceof Node && group.contains(e.relatedTarget))
  if (fromOutside && isKeyboardFocus(radio)) {
    // Tabbing into a group lands on the chosen option, per the ARIA pattern.
    const checked = groupRadios(group).find((r) => r.getAttribute('aria-checked') === 'true')
    if (checked && checked !== radio) {
      applyRovingTabIndex(group, checked)
      checked.focus()
      return
    }
  }
  applyRovingTabIndex(group, radio)
}

const NEXT_KEYS: Record<string, 1 | -1 | 'first' | 'last'> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
  Home: 'first',
  End: 'last',
}

function onRadioKeyDown(e: KeyboardEvent) {
  if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return
  const step = NEXT_KEYS[e.key]
  if (step === undefined) return
  const hit = radioOf(e.target)
  if (!hit) return
  const radios = groupRadios(hit.group)
  if (radios.length < 2) return
  const i = radios.indexOf(hit.radio)
  const next =
    step === 'first' ? radios[0]
    : step === 'last' ? radios[radios.length - 1]
    : radios[(i + step + radios.length) % radios.length]
  e.preventDefault()
  applyRovingTabIndex(hit.group, next)
  next.focus()
  // Selection follows focus (ARIA radio group pattern).
  if (next.getAttribute('aria-checked') !== 'true') {
    next.click()
    keepFocusAcrossRemount(next)
  }
}

const labelOf = (el: Element) => (el.getAttribute('aria-label') ?? el.textContent ?? '').trim()

/**
 * Some radio groups switch routes (Contacts ⇄ Organizations), which remounts
 * the group and drops focus on <body>. Put focus back on the same option in
 * the new group so the keyboard user doesn't start over from the top.
 */
function keepFocusAcrossRemount(option: HTMLElement) {
  const label = labelOf(option)
  const restore = () => {
    if (option.isConnected || (document.activeElement && document.activeElement !== document.body)) return
    const twin = Array.from(document.querySelectorAll<HTMLElement>(RADIO)).find((r) => labelOf(r) === label)
    if (twin) {
      const group = twin.closest(GROUP)
      if (group) applyRovingTabIndex(group, twin)
      twin.focus()
    }
  }
  requestAnimationFrame(() => requestAnimationFrame(restore))
  window.setTimeout(restore, 250)
}

function scrollsX(el: HTMLElement): boolean {
  if (el.scrollWidth <= el.clientWidth + 1) return false
  const ox = getComputedStyle(el).overflowX
  return ox === 'auto' || ox === 'scroll'
}

/** How far `strip` must scroll left/right so `el` sits `pad` px inside its edges. */
export function stripRevealDelta(el: DOMRect, strip: DOMRect, pad = STRIP_REVEAL_PAD): number {
  const room = strip.width - 2 * pad
  if (el.width > room) return el.left - (strip.left + pad) // too wide: align its start
  if (el.left < strip.left + pad) return el.left - (strip.left + pad)
  if (el.right > strip.right - pad) return el.right - (strip.right - pad)
  return 0
}

function onRevealFocusIn(e: FocusEvent) {
  const el = e.target
  if (!(el instanceof HTMLElement) || !isKeyboardFocus(el)) return
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    if (!scrollsX(p)) continue
    const dx = stripRevealDelta(el.getBoundingClientRect(), p.getBoundingClientRect())
    if (dx !== 0) {
      const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      p.scrollBy({ left: dx, behavior: reduce ? 'auto' : 'smooth' })
    }
    return
  }
}

let installed: (() => void) | null = null

/** Install the delegated listeners once. Returns an uninstall function (tests). */
export function installFocusAssist(doc: Document = document): () => void {
  if (installed) return installed
  doc.addEventListener('focusin', onRadioFocusIn)
  doc.addEventListener('focusin', onRevealFocusIn)
  doc.addEventListener('keydown', onRadioKeyDown)
  installed = () => {
    doc.removeEventListener('focusin', onRadioFocusIn)
    doc.removeEventListener('focusin', onRevealFocusIn)
    doc.removeEventListener('keydown', onRadioKeyDown)
    installed = null
  }
  return installed
}
